import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Assessment,
  Person,
  Business,
  CalendarToday,
  AccessTime,
  CheckCircle,
  Schedule,
  LocationOn,
  Cancel,
  FilterList,
  DateRange,
  Today,
  Clear,
  ViewModule,
  ViewList,
  ViewDay,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../services/api";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Card from "../components/ui/Card";
import Avatar from "../components/ui/Avatar";

const STATUS_CONFIG = {
  present: {
    label: "Present",
    color: "bg-green-100 text-green-700 border-green-200",
  },
  absent: { label: "Absent", color: "bg-red-100 text-red-700 border-red-200" },
  "half-day": {
    label: "Half Day",
    color: "bg-yellow-100 text-yellow-700 border-yellow-200",
  },
  "in-progress": {
    label: "In Progress",
    color: "bg-blue-100 text-blue-700 border-blue-200",
  },
};

const getStatusPresentation = (status) => {
  const normalized = status?.toLowerCase?.();
  if (normalized && STATUS_CONFIG[normalized]) {
    return STATUS_CONFIG[normalized];
  }
  const fallbackLabel = status ? status.replace(/-/g, " ") : "Unknown";
  return {
    label: fallbackLabel,
    color: "bg-gray-100 text-gray-700 border-gray-200",
  };
};

const FILTER_TYPES = {
  TODAY: "today",
  MONTH_1: "1month",
  MONTH_3: "3months",
  MONTH_6: "6months",
  MONTH_12: "12months",
  CUSTOM: "custom",
};

const VIEW_TYPES = {
  GRID: "grid",
  DAYWISE: "daywise",
  EMPLOYEEWISE: "employeewise",
  TABLE: "table",
};

export default function AttendanceReport() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(FILTER_TYPES.TODAY);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [viewType, setViewType] = useState(VIEW_TYPES.DAYWISE);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [detailTotalMs, setDetailTotalMs] = useState(0);

  useEffect(() => {
    fetchEmployees();
    // Initialize with today's date
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);
  }, []);

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedDate, startDate, endDate, selectedEmployee, filterType]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      const payload = response.data;
      const employeeList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setEmployees(employeeList);
    } catch (err) {
      console.error("Failed to fetch employees", err);
    }
  };

  const applyDateFilter = (type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split("T")[0];

    switch (type) {
      case FILTER_TYPES.TODAY:
        setFilterType(FILTER_TYPES.TODAY);
        setSelectedDate(todayString);
        setStartDate("");
        setEndDate("");
        break;
      case FILTER_TYPES.MONTH_1:
        setFilterType(FILTER_TYPES.MONTH_1);
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        setStartDate(oneMonthAgo.toISOString().split("T")[0]);
        setEndDate(todayString);
        setSelectedDate("");
        break;
      case FILTER_TYPES.MONTH_3:
        setFilterType(FILTER_TYPES.MONTH_3);
        const threeMonthsAgo = new Date(today);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        setStartDate(threeMonthsAgo.toISOString().split("T")[0]);
        setEndDate(todayString);
        setSelectedDate("");
        break;
      case FILTER_TYPES.MONTH_6:
        setFilterType(FILTER_TYPES.MONTH_6);
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        setStartDate(sixMonthsAgo.toISOString().split("T")[0]);
        setEndDate(todayString);
        setSelectedDate("");
        break;
      case FILTER_TYPES.MONTH_12:
        setFilterType(FILTER_TYPES.MONTH_12);
        const twelveMonthsAgo = new Date(today);
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        setStartDate(twelveMonthsAgo.toISOString().split("T")[0]);
        setEndDate(todayString);
        setSelectedDate("");
        break;
      case FILTER_TYPES.CUSTOM:
        setFilterType(FILTER_TYPES.CUSTOM);
        setSelectedDate("");
        break;
      default:
        break;
    }
  };

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const params = {};

      // Use date range if custom range is selected, otherwise use single date
      if (filterType === FILTER_TYPES.CUSTOM) {
        if (startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        } else if (startDate) {
          // If only start date is provided, use it as single date
          params.date = startDate;
        }
      } else if (filterType === FILTER_TYPES.TODAY && selectedDate) {
        params.date = selectedDate;
      } else if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      if (selectedEmployee) {
        params.employeeId = selectedEmployee;
      }

      const response = await api.get("/attendance/report", { params });
      const payload = response.data;
      const records = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      // Debug: Log first record to check profile_image
      if (records.length > 0) {
        console.log("Attendance report data sample:", {
          employee: records[0].employee,
          user: records[0].employee?.user,
          profile_image: records[0].employee?.user?.profile_image,
        });
      }

      setAttendanceData(records);
    } catch (err) {
      toast.error("Failed to fetch attendance data");
      console.error("Failed to fetch attendance", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (record) => {
    setDetailsDialog(true);
    setSelectedRecord(record);
    setLogEntries([]);
    await fetchLocationLogs(record);
  };

  const handleCloseDetails = () => {
    setDetailsDialog(false);
    setSelectedRecord(null);
    setLogEntries([]);
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    setFilterType(FILTER_TYPES.TODAY);
    setSelectedDate(today);
    setStartDate("");
    setEndDate("");
    setSelectedEmployee("");
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const calculateDurationMs = (startTime, endTime, includeActive = false) => {
    if (!startTime) return 0;
    const start = new Date(startTime).getTime();
    if (Number.isNaN(start)) return 0;
    if (endTime) {
      const end = new Date(endTime).getTime();
      if (Number.isNaN(end) || end <= start) return 0;
      return end - start;
    }
    if (!includeActive) return 0;
    const now = Date.now();
    return now > start ? now - start : 0;
  };

  const formatDurationLabel = (milliseconds) => {
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
      return "0h 00m 00s";
    }
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h ${minutes.toString().padStart(2, "0")}m ${seconds
      .toString()
      .padStart(2, "0")}s`;
  };

  const openInMaps = (location) => {
    const { latitude, longitude } = location || {};
    if (!latitude || !longitude) return;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fetchLocationLogs = async (record) => {
    const userId = record?.employee?.user?._id;
    const dateValue = record?.date;
    if (!userId || !dateValue) {
      setLogEntries([]);
      return;
    }

    try {
      const dateParam =
        typeof dateValue === "string"
          ? dateValue.split("T")[0]
          : new Date(dateValue).toISOString().split("T")[0];
      const response = await api.get("/attendance/logs", {
        params: {
          userId,
          date: selectedDate,
        },
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setLogEntries(data);
    } catch (err) {
      console.error("Failed to fetch location logs", err);
      toast.error("Failed to load attendance locations");
      setLogEntries([]);
    }
  };

  const findLogEntry = useCallback(
    (action, timestamp) => {
      if (!timestamp || !logEntries.length) return null;
      const targetTime = new Date(timestamp).getTime();
      if (Number.isNaN(targetTime)) return null;

      let closest = null;
      let smallestDiff = Number.POSITIVE_INFINITY;

      logEntries.forEach((entry) => {
        if (entry.action !== action || !entry.timestamp) return;
        const entryTime = new Date(entry.timestamp).getTime();
        if (Number.isNaN(entryTime)) return;
        const diff = Math.abs(entryTime - targetTime);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closest = entry;
        }
      });

      return smallestDiff <= 5 * 60 * 1000 ? closest : null;
    },
    [logEntries]
  );

  const computedSessions = useMemo(() => {
    if (selectedRecord?.sessions?.length) {
      return selectedRecord.sessions;
    }

    if (!logEntries.length) {
      return [];
    }

    const sortedLogs = [...logEntries]
      .filter((entry) => entry && entry.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const sessions = [];
    let current = null;

    sortedLogs.forEach((entry) => {
      const action = entry.action?.toLowerCase?.();
      const time = entry.timestamp;
      if (!action || !time) return;

      if (action === "checkin") {
        if (current) {
          sessions.push({ ...current });
        }
        current = { clockIn: time, clockOut: null };
      } else if (action === "checkout") {
        if (current && !current.clockOut) {
          current.clockOut = time;
          sessions.push({ ...current });
          current = null;
        } else {
          sessions.push({ clockIn: null, clockOut: time });
        }
      }
    });

    if (current) {
      sessions.push({ ...current });
    }

    return sessions.map((session, index) => ({
      order: index + 1,
      clockIn: session.clockIn,
      clockOut: session.clockOut,
    }));
  }, [selectedRecord, logEntries]);

  useEffect(() => {
    if (!detailsDialog) {
      setDetailTotalMs(0);
      return undefined;
    }

    if (!computedSessions.length) {
      setDetailTotalMs(0);
      return undefined;
    }

    const updateTotals = () => {
      const total = computedSessions.reduce((sum, session) => {
        const includeActive = Boolean(session.clockIn) && !session.clockOut;
        return (
          sum +
          calculateDurationMs(session.clockIn, session.clockOut, includeActive)
        );
      }, 0);
      setDetailTotalMs(total);
    };

    updateTotals();

    const hasActiveSession = computedSessions.some(
      (session) => session.clockIn && !session.clockOut
    );

    if (!hasActiveSession) {
      return undefined;
    }

    const intervalId = setInterval(updateTotals, 1000);
    return () => clearInterval(intervalId);
  }, [detailsDialog, computedSessions]);

  const getSessionCount = (record) =>
    typeof record.sessionCount === "number"
      ? record.sessionCount
      : record.sessions?.length || 0;

  // Group data by date
  const groupedByDate = useMemo(() => {
    const grouped = new Map();
    attendanceData.forEach((record) => {
      const dateKey = record.date
        ? new Date(record.date).toISOString().split("T")[0]
        : "unknown";
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey).push(record);
    });
    // Sort dates descending
    return Array.from(grouped.entries()).sort(
      (a, b) => new Date(b[0]) - new Date(a[0])
    );
  }, [attendanceData]);

  // Group data by employee
  const groupedByEmployee = useMemo(() => {
    const grouped = new Map();
    attendanceData.forEach((record) => {
      const empId = record.employee?._id || "unknown";
      if (!grouped.has(empId)) {
        grouped.set(empId, {
          employee: record.employee,
          records: [],
        });
      }
      grouped.get(empId).records.push(record);
    });
    // Sort by employee name
    return Array.from(grouped.values()).sort((a, b) => {
      const nameA = a.employee?.user?.name || "";
      const nameB = b.employee?.user?.name || "";
      return nameA.localeCompare(nameB);
    });
  }, [attendanceData]);

  const toggleDay = (dateKey) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dateKey)) {
      newExpanded.delete(dateKey);
    } else {
      newExpanded.add(dateKey);
    }
    setExpandedDays(newExpanded);
  };

  const toggleEmployee = (empId) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(empId)) {
      newExpanded.delete(empId);
    } else {
      newExpanded.add(empId);
    }
    setExpandedEmployees(newExpanded);
  };

  // Helper function to render attendance card
  const renderAttendanceCard = (record, index = 0) => {
    const sessionCount = getSessionCount(record);
    const sessionTotalMs = (record.sessions || []).reduce((sum, session) => {
      const includeActive = Boolean(session.clockIn) && !session.clockOut;
      return (
        sum +
        calculateDurationMs(session.clockIn, session.clockOut, includeActive)
      );
    }, 0);

    const recordTotalMs =
      sessionTotalMs ||
      (Number.isFinite(record.totalHours)
        ? record.totalHours * 3600 * 1000
        : 0);

    const statusPresentation = getStatusPresentation(record.status);

    return (
      <motion.div
        key={`${
          record.employee?._id || record.employee?.user?._id || "employee"
        }-${record.date || "no-date"}-${index}`}
        variants={cardVariants}
        className="card group cursor-pointer"
        whileHover={{ y: -8, transition: { duration: 0.2 } }}
        onClick={() => handleViewDetails(record)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <Avatar
              src={record.employee?.user?.profile_image}
              name={record.employee?.user?.name || "E"}
              size="md"
              className="rounded-2xl"
            />
            <div>
              <h3 className="text-xl font-bold text-primary mb-1">
                {record.employee?.user?.name || "N/A"}
              </h3>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Business className="w-4 h-4" />
                {record.employee?.department?.name || "N/A"}
              </p>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusPresentation.color}`}
          >
            {statusPresentation.label}
          </span>
        </div>

        <div className="space-y-3 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <CalendarToday className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                {record.date ? new Date(record.date).toLocaleDateString() : "-"}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">First Clock-In</p>
              <p className="text-sm font-semibold text-gray-700">
                {formatTime(record.firstClockIn)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Last Clock-Out</p>
              <p className="text-sm font-semibold text-gray-700">
                {record.lastClockOut
                  ? formatTime(record.lastClockOut)
                  : record.status?.toLowerCase() === "in-progress"
                  ? "In Progress"
                  : "-"}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <AccessTime className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">
                {sessionCount} {sessionCount === 1 ? "session" : "sessions"}
              </span>
            </div>
            <span className="text-lg font-bold text-primary">
              {formatDurationLabel(recordTotalMs)}
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold text-primary mb-2">
          Employee Attendance Report
        </h1>
        <p className="text-gray-600">
          View detailed attendance reports for employees
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-6"
      >
        <div className="space-y-6">
          {/* Quick Filter Buttons */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FilterList className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-semibold text-gray-700">
                Quick Filters
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => applyDateFilter(FILTER_TYPES.TODAY)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                  filterType === FILTER_TYPES.TODAY
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Today className="w-4 h-4" />
                Today
              </button>
              <button
                onClick={() => applyDateFilter(FILTER_TYPES.MONTH_1)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  filterType === FILTER_TYPES.MONTH_1
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                1 Month
              </button>
              <button
                onClick={() => applyDateFilter(FILTER_TYPES.MONTH_3)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  filterType === FILTER_TYPES.MONTH_3
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                3 Months
              </button>
              <button
                onClick={() => applyDateFilter(FILTER_TYPES.MONTH_6)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  filterType === FILTER_TYPES.MONTH_6
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                6 Months
              </button>
              <button
                onClick={() => applyDateFilter(FILTER_TYPES.MONTH_12)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  filterType === FILTER_TYPES.MONTH_12
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                12 Months
              </button>
              <button
                onClick={() => applyDateFilter(FILTER_TYPES.CUSTOM)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                  filterType === FILTER_TYPES.CUSTOM
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <DateRange className="w-4 h-4" />
                Custom Range
              </button>
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filterType === FILTER_TYPES.CUSTOM ? (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarToday className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Start Date
                    </label>
                  </div>
                  <Input
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarToday className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      End Date
                    </label>
                  </div>
                  <Input
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || ""}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarToday className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    {filterType === FILTER_TYPES.TODAY ? "Date" : "Date Range"}
                  </label>
                </div>
                {filterType === FILTER_TYPES.TODAY ? (
                  <Input
                    type="date"
                    value={selectedDate || ""}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                  />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 min-h-[42px]">
                    <DateRange className="w-4 h-4" />
                    <span>
                      {startDate && endDate
                        ? `${new Date(
                            startDate
                          ).toLocaleDateString()} - ${new Date(
                            endDate
                          ).toLocaleDateString()}`
                        : filterType === FILTER_TYPES.MONTH_1
                        ? "Last 1 Month"
                        : filterType === FILTER_TYPES.MONTH_3
                        ? "Last 3 Months"
                        : filterType === FILTER_TYPES.MONTH_6
                        ? "Last 6 Months"
                        : filterType === FILTER_TYPES.MONTH_12
                        ? "Last 12 Months"
                        : "Select date range"}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Employee Filter */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Person className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Employee
                </label>
              </div>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="input-field w-full"
              >
                <option value="">All Employees</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.user_id?.name || "Unknown"} -{" "}
                    {emp.designation || "Employee"}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="w-full flex items-center justify-center gap-2"
              >
                <Clear className="w-4 h-4" />
                Reset Filters
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Summary Card */}
      {!loading && attendanceData.length > 0 && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Assessment className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Records</p>
                  <p className="text-2xl font-bold text-primary">
                    {attendanceData.length}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CalendarToday className="w-4 h-4" />
                  <span className="font-semibold">Period:</span>
                  <span>
                    {filterType === FILTER_TYPES.TODAY && selectedDate
                      ? new Date(selectedDate).toLocaleDateString()
                      : startDate && endDate
                      ? `${new Date(
                          startDate
                        ).toLocaleDateString()} - ${new Date(
                          endDate
                        ).toLocaleDateString()}`
                      : "All Time"}
                  </span>
                </div>
                {selectedEmployee && (
                  <div className="flex items-center gap-2">
                    <Person className="w-4 h-4" />
                    <span className="font-semibold">Employee:</span>
                    <span>
                      {employees.find((e) => e._id === selectedEmployee)
                        ?.user_id?.name || "Selected"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* View Type Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="card mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ViewModule className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  View Type
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewType(VIEW_TYPES.DAYWISE)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                    viewType === VIEW_TYPES.DAYWISE
                      ? "bg-primary text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <ViewDay className="w-4 h-4" />
                  Day-wise
                </button>
                <button
                  onClick={() => setViewType(VIEW_TYPES.EMPLOYEEWISE)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                    viewType === VIEW_TYPES.EMPLOYEEWISE
                      ? "bg-primary text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <Person className="w-4 h-4" />
                  Employee-wise
                </button>
                <button
                  onClick={() => setViewType(VIEW_TYPES.GRID)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                    viewType === VIEW_TYPES.GRID
                      ? "bg-primary text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <ViewModule className="w-4 h-4" />
                  Grid
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Attendance Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 skeleton rounded-2xl"></div>
                <div className="w-20 h-6 skeleton rounded"></div>
              </div>
              <div className="h-4 skeleton rounded w-full mb-2"></div>
              <div className="h-4 skeleton rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : attendanceData.length === 0 ? (
        <div className="card text-center py-12">
          <Assessment className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No attendance records found</p>
        </div>
      ) : (
        <>
          {/* Render based on view type */}
          {viewType === VIEW_TYPES.DAYWISE && (
            <div className="space-y-4">
              {groupedByDate.map(([dateKey, records]) => {
                const isExpanded = expandedDays.has(dateKey);
                const dateObj = new Date(dateKey);
                const presentCount = records.filter(
                  (r) => r.status?.toLowerCase() === "present"
                ).length;
                const absentCount = records.filter(
                  (r) => r.status?.toLowerCase() === "absent"
                ).length;

                return (
                  <motion.div
                    key={dateKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card"
                  >
                    <button
                      onClick={() => toggleDay(dateKey)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                          <CalendarToday className="w-6 h-6 text-primary" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-bold text-primary">
                            {dateObj.toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {records.length} records • {presentCount} Present •{" "}
                            {absentCount} Absent
                          </p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ExpandLess className="w-6 h-6 text-gray-400" />
                      ) : (
                        <ExpandMore className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {records.map((record) =>
                            renderAttendanceCard(record)
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          {viewType === VIEW_TYPES.EMPLOYEEWISE && (
            <div className="space-y-4">
              {groupedByEmployee.map((group) => {
                const empId = group.employee?._id || "unknown";
                const isExpanded = expandedEmployees.has(empId);
                const presentCount = group.records.filter(
                  (r) => r.status?.toLowerCase() === "present"
                ).length;
                const absentCount = group.records.filter(
                  (r) => r.status?.toLowerCase() === "absent"
                ).length;

                return (
                  <motion.div
                    key={empId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card"
                  >
                    <button
                      onClick={() => toggleEmployee(empId)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <Avatar
                          src={group.employee?.user?.profile_image}
                          name={group.employee?.user?.name || "E"}
                          size="md"
                          className="rounded-2xl"
                        />
                        <div className="text-left">
                          <h3 className="text-lg font-bold text-primary">
                            {group.employee?.user?.name || "N/A"}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {group.records.length} records • {presentCount}{" "}
                            Present • {absentCount} Absent
                          </p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ExpandLess className="w-6 h-6 text-gray-400" />
                      ) : (
                        <ExpandMore className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {group.records
                            .sort(
                              (a, b) =>
                                new Date(b.date || 0) - new Date(a.date || 0)
                            )
                            .map((record) => renderAttendanceCard(record))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          {viewType === VIEW_TYPES.GRID && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {attendanceData.map((record, index) => {
                return renderAttendanceCard(record, index);
              })}
            </motion.div>
          )}
        </>
      )}

      {/* Details Dialog */}
      <AnimatePresence>
        {detailsDialog && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseDetails}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Dialog Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-primary">
                    Session Details
                  </h2>
                  <button
                    onClick={handleCloseDetails}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Dialog Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {selectedRecord && (
                    <div className="space-y-6">
                      {/* Employee Info Card */}
                      <Card>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Person className="w-5 h-5 text-gray-400" />
                              <label className="text-xs text-gray-500">
                                Employee
                              </label>
                            </div>
                            <p className="text-base font-semibold text-gray-900">
                              {selectedRecord.employee?.user?.name || "N/A"}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Business className="w-5 h-5 text-gray-400" />
                              <label className="text-xs text-gray-500">
                                Department
                              </label>
                            </div>
                            <p className="text-base font-semibold text-gray-900">
                              {selectedRecord.employee?.department?.name ||
                                "N/A"}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <CalendarToday className="w-5 h-5 text-gray-400" />
                              <label className="text-xs text-gray-500">
                                Date
                              </label>
                            </div>
                            <p className="text-base font-semibold text-gray-900">
                              {selectedRecord.date
                                ? new Date(
                                    selectedRecord.date
                                  ).toLocaleDateString()
                                : "-"}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <AccessTime className="w-5 h-5 text-gray-400" />
                              <label className="text-xs text-gray-500">
                                Total Hours
                              </label>
                            </div>
                            <p className="text-2xl font-bold text-primary">
                              {formatDurationLabel(detailTotalMs)}
                            </p>
                          </div>
                        </div>
                      </Card>

                      {/* Sessions List */}
                      <div>
                        <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                          <Schedule className="w-6 h-6" />
                          Clock-In/Out Sessions
                        </h3>
                        <div className="space-y-4">
                          {computedSessions.length > 0 ? (
                            computedSessions.map((session, index) => {
                              const checkInLog = findLogEntry(
                                "checkin",
                                session.clockIn
                              );
                              const checkOutLog = session.clockOut
                                ? findLogEntry("checkout", session.clockOut)
                                : null;

                              return (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                >
                                  <Card>
                                    <div className="flex items-center justify-between mb-4">
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white font-bold">
                                          {session.order || index + 1}
                                        </div>
                                        <div>
                                          <p className="text-sm font-semibold text-gray-700">
                                            Session {session.order || index + 1}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {formatDurationLabel(
                                              calculateDurationMs(
                                                session.clockIn,
                                                session.clockOut
                                              )
                                            )}
                                          </p>
                                        </div>
                                      </div>
                                      <span className="text-lg font-bold text-primary">
                                        {formatDurationLabel(
                                          calculateDurationMs(
                                            session.clockIn,
                                            session.clockOut
                                          )
                                        )}
                                      </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                                      <div className="p-4 bg-green-50 rounded-2xl border border-green-200">
                                        <div className="flex items-center gap-2 mb-2">
                                          <CheckCircle className="w-4 h-4 text-green-600" />
                                          <label className="text-xs font-semibold text-gray-700">
                                            Clock-In
                                          </label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-base font-bold text-gray-900">
                                            {formatTime(session.clockIn)}
                                          </span>
                                          {checkInLog?.location?.latitude &&
                                            checkInLog?.location?.longitude && (
                                              <button
                                                onClick={() =>
                                                  openInMaps(
                                                    checkInLog.location
                                                  )
                                                }
                                                className="p-1.5 hover:bg-green-100 rounded-lg transition-colors"
                                                title="View location on map"
                                              >
                                                <LocationOn className="w-5 h-5 text-green-600" />
                                              </button>
                                            )}
                                        </div>
                                      </div>

                                      <div
                                        className={`p-4 rounded-2xl border ${
                                          session.clockOut
                                            ? "bg-red-50 border-red-200"
                                            : "bg-yellow-50 border-yellow-200"
                                        }`}
                                      >
                                        <div className="flex items-center gap-2 mb-2">
                                          {session.clockOut ? (
                                            <Cancel className="w-4 h-4 text-red-600" />
                                          ) : (
                                            <Schedule className="w-4 h-4 text-yellow-600" />
                                          )}
                                          <label className="text-xs font-semibold text-gray-700">
                                            Clock-Out
                                          </label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {session.clockOut ? (
                                            <>
                                              <span className="text-base font-bold text-gray-900">
                                                {formatTime(session.clockOut)}
                                              </span>
                                              {checkOutLog?.location
                                                ?.latitude &&
                                                checkOutLog?.location
                                                  ?.longitude && (
                                                  <button
                                                    onClick={() =>
                                                      openInMaps(
                                                        checkOutLog.location
                                                      )
                                                    }
                                                    className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                                                    title="View location on map"
                                                  >
                                                    <LocationOn className="w-5 h-5 text-red-600" />
                                                  </button>
                                                )}
                                            </>
                                          ) : (
                                            <span className="px-3 py-1 bg-yellow-200 text-yellow-800 text-xs font-semibold rounded-full">
                                              In Progress
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </Card>
                                </motion.div>
                              );
                            })
                          ) : (
                            <div className="card text-center py-12">
                              <Schedule className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-500 text-lg">
                                No sessions found
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dialog Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <Button variant="primary" onClick={handleCloseDetails}>
                    Close
                  </Button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
